/** @jsxImportSource @emotion/react */
import { Trans, useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { TUTORIAL_IDS } from "@/shared";
import LottieAnimation from "@/shared/components/animation/LottieAnimation";

import companyCardAnimation from "../../assets/animations/company_card_tutorial.json";

import { useStyles } from "./styles/styles";
import { TutorialStep } from "./types";

const useTutorialStepGroups = (fromMd: boolean, toSm: boolean) => {
  const { t } = useTranslation();
  const { companyCardAnimationCSS } = useStyles();

  const companySelectorBtnId = toSm
    ? TUTORIAL_IDS.CompanySelectorButtonToSm
    : TUTORIAL_IDS.CompanySelectorButton;

  const companySelectorStepsDesktop: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.CompanySelectorButton}`,
      highlightId: TUTORIAL_IDS.CompanySelectorButton,
      title: t("Tutorials##company selector##desktop##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##company selector##desktop##first step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.CompanySelectorMenu}`,
      highlightId: TUTORIAL_IDS.CompanySelectorMenu,
      title: t("Tutorials##company selector##desktop##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##company selector##desktop##second step##content" />
      ),
      placement: "left",
      spotlightPadding: 1,
      overlayClickAction: false,
      dismissKeyAction: false
    }
  ];

  const companySelectorStepsMobile: TutorialStep[] = [
    {
      target: `#${companySelectorBtnId}`,
      highlightId: companySelectorBtnId,
      title: t("Tutorials##company selector##mobile##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##company selector##mobile##first step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.CompanySelectorContent}`,
      highlightId: TUTORIAL_IDS.CompanySelectorContent,
      title: t("Tutorials##company selector##mobile##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##company selector##mobile##second step##content" />
      ),
      placement: "center",
      overlayClickAction: false,
      dismissKeyAction: false
    }
  ];

  const addNewCompanyCardStepsDesktop: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.CompanyCardAuthButton}`,
      highlightId: TUTORIAL_IDS.CompanyCardAuthButton,
      title: t("Tutorials##add company card##desktop##first step##title"),
      content: (
        <>
          <LottieAnimation
            animationData={companyCardAnimation}
            loop
            css={css(companyCardAnimationCSS)}
          />
          <Trans i18nKey="Tutorials##add company card##desktop##first step##content" />
        </>
      ),
      placement: "left",
      spotlightPadding: -5
    },
    {
      target: `#${TUTORIAL_IDS.Companies}`,
      highlightId: TUTORIAL_IDS.Companies,
      title: t("Tutorials##add company card##desktop##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##desktop##second step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Row0}`,
      highlightId: TUTORIAL_IDS.Row0,
      title: t("Tutorials##add company card##desktop##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##desktop##third step##content" />
      ),
      placement: "bottom",
      spotlightPadding: -1,
      skipScroll: false,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.DesktopDetailsAside}`,
      highlightId: TUTORIAL_IDS.DesktopDetailsAside,
      title: t("Tutorials##add company card##desktop##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##desktop##fourth step##content" />
      ),
      placement: "left"
    },
    {
      target: `#${TUTORIAL_IDS.AddNewCompanyCardComponent}`,
      highlightId: TUTORIAL_IDS.AddNewCompanyCardComponent,
      title: t("Tutorials##add company card##desktop##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##desktop##fifth step##content" />
      ),
      placement: "bottom",
      skipScroll: false,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.AccordionDetailsCard}`,
      highlightId: TUTORIAL_IDS.AccordionDetailsCard,
      title: t("Tutorials##add company card##desktop##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##desktop##final step##content" />
      ),
      placement: "left",
      spotlightPadding: 5,
      skipScroll: false
    }
  ];

  const addNewCompanyCardStepsMobile: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.MobileDrawerButton}`,
      highlightId: TUTORIAL_IDS.MobileDrawerButton,
      title: t("Tutorials##add company card##mobile##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##first step##content" />
      ),
      placement: "left",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Companies}`,
      highlightId: TUTORIAL_IDS.Companies,
      title: t("Tutorials##add company card##mobile##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##second step##content" />
      ),
      placement: "top",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.MobileDetails0Item}`,
      highlightId: TUTORIAL_IDS.MobileDetails0Item,
      title: t("Tutorials##add company card##mobile##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##third step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##add company card##mobile##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##fourth step##content" />
      ),
      placement: "center"
    },
    {
      target: `#${TUTORIAL_IDS.AddNewCompanyCardComponent}`,
      highlightId: TUTORIAL_IDS.AddNewCompanyCardComponent,
      title: t("Tutorials##add company card##mobile##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##fifth step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false,
      skipScroll: false
    },
    {
      target: `#${TUTORIAL_IDS.AccordionDetailsCard}`,
      highlightId: TUTORIAL_IDS.AccordionDetailsCard,
      title: t("Tutorials##add company card##mobile##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##add company card##mobile##final step##content" />
      ),
      placement: "top",
      skipScroll: false
    }
  ];

  const addVehicleStep: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.AddResourceButton}`,
      highlightId: TUTORIAL_IDS.AddResourceButton,
      title: t("Tutorials##add vehicle##first step##title"),
      content: <Trans i18nKey="Tutorials##add vehicle##first step##content" />,
      placement: fromMd ? "left" : "bottom",
      spotlightPadding: 2,
      blockTargetInteraction: false
    }
  ];

  const applyScheduleStepsDesktop: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.Vehicles}`,
      highlightId: TUTORIAL_IDS.Vehicles,
      title: t("Tutorials##apply schedule##desktop##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##first step##content" />
      ),
      placement: "bottom",
      spotlightPadding: 1,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Checkbox0VehiclesBody}`,
      highlightId: TUTORIAL_IDS.Checkbox0VehiclesBody,
      title: t("Tutorials##apply schedule##desktop##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##second step##content" />
      ),
      placement: "right",
      spotlightPadding: -2,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesDialogButton}`,
      highlightId: TUTORIAL_IDS.SchedulesDialogButton,
      title: t("Tutorials##apply schedule##desktop##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##third step##content" />
      ),
      spotlightPadding: 3,
      placement: "right",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.MainScheduleCards}`,
      highlightId: TUTORIAL_IDS.MainScheduleCards,
      title: t("Tutorials##apply schedule##desktop##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##fourth step##content" />
      ),
      spotlightPadding: 4,
      placement: "top"
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesMainDialogApplyButton}`,
      highlightId: TUTORIAL_IDS.SchedulesMainDialogApplyButton,
      title: t("Tutorials##apply schedule##desktop##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##fifth step##content" />
      ),
      spotlightPadding: 2,
      placement: "top"
    },
    {
      target: `#${TUTORIAL_IDS.AccordionDetailsCard}`,
      highlightId: TUTORIAL_IDS.AccordionDetailsCard,
      title: t("Tutorials##apply schedule##desktop##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##desktop##final step##content" />
      ),
      placement: "left",
      spotlightPadding: 5
    }
  ];

  const applyScheduleStepsMobile: TutorialStep[] = [
    {
      target: `#${TUTORIAL_IDS.MobileDrawerButton}`,
      highlightId: TUTORIAL_IDS.MobileDrawerButton,
      title: t("Tutorials##apply schedule##mobile##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##first step##content" />
      ),
      placement: "bottom",
      spotlightPadding: 1,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Vehicles}`,
      highlightId: TUTORIAL_IDS.Vehicles,
      title: t("Tutorials##apply schedule##mobile##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##second step##content" />
      ),
      placement: "bottom",
      spotlightPadding: -2,
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.InactiveMobileHead}`,
      highlightId: TUTORIAL_IDS.InactiveMobileHead,
      title: t("Tutorials##apply schedule##mobile##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##third step##content" />
      ),
      spotlightPadding: 1,
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Checkbox0VehiclesBody}`,
      highlightId: TUTORIAL_IDS.Checkbox0VehiclesBody,
      title: t("Tutorials##apply schedule##mobile##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##fourth step##content" />
      ),
      placement: "top",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesDialogButton}`,
      highlightId: TUTORIAL_IDS.SchedulesDialogButton,
      title: t("Tutorials##apply schedule##mobile##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##fifth step##content" />
      ),
      spotlightPadding: 2,
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##apply schedule##mobile##sixth step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##sixth step##content" />
      ),
      placement: "center"
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesMainDialogApplyButton}`,
      highlightId: TUTORIAL_IDS.SchedulesMainDialogApplyButton,
      title: t("Tutorials##apply schedule##mobile##seventh step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##seventh step##content" />
      ),
      placement: "top",
      spotlightPadding: 2
    },
    {
      target: `#${TUTORIAL_IDS.MobileDetails0Item}`,
      highlightId: TUTORIAL_IDS.MobileDetails0Item,
      title: t("Tutorials##apply schedule##mobile##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##apply schedule##mobile##final step##content" />
      ),
      placement: "top"
    }
  ];

  const createScheduleStepsDesktop: TutorialStep[] = [
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##create schedule##desktop##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##first step##content" />
      ),
      placement: "center"
    },
    {
      target: `#${TUTORIAL_IDS.Vehicles}`,
      highlightId: TUTORIAL_IDS.Vehicles,
      title: t("Tutorials##create schedule##desktop##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##second step##content" />
      ),
      placement: "bottom-start",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesDialogButton}`,
      highlightId: TUTORIAL_IDS.SchedulesDialogButton,
      title: t("Tutorials##create schedule##desktop##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##third step##content" />
      ),
      spotlightPadding: 1,
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: ".MuiDialog-paper",
      highlightId: TUTORIAL_IDS.SchedulesMainDialog,
      title: t("Tutorials##create schedule##desktop##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##fourth step##content" />
      ),
      spotlightPadding: -1,
      placement: "top"
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesMainDialogApplyButton}`,
      highlightId: TUTORIAL_IDS.SchedulesMainDialogApplyButton,
      title: t("Tutorials##create schedule##desktop##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##fifth step##content" />
      ),

      placement: "top"
    },
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##create schedule##desktop##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##desktop##final step##content" />
      ),
      placement: "center"
    }
  ];

  const createScheduleStepsMobile: TutorialStep[] = [
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##create schedule##mobile##first step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##first step##content" />
      ),
      placement: "center"
    },
    {
      target: `#${TUTORIAL_IDS.MobileDrawerButton}`,
      highlightId: TUTORIAL_IDS.MobileDrawerButton,
      title: t("Tutorials##create schedule##mobile##second step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##second step##content" />
      ),
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.Vehicles}`,
      highlightId: TUTORIAL_IDS.Vehicles,
      title: t("Tutorials##create schedule##mobile##third step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##third step##content" />
      ),
      placement: "top",
      blockTargetInteraction: false
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesDialogButton}`,
      highlightId: TUTORIAL_IDS.SchedulesDialogButton,
      title: t("Tutorials##create schedule##mobile##fourth step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##fourth step##content" />
      ),
      spotlightPadding: 2,
      placement: "bottom",
      blockTargetInteraction: false
    },
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##create schedule##mobile##fifth step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##fifth step##content" />
      ),
      placement: "center"
    },
    {
      target: `#${TUTORIAL_IDS.SchedulesMainDialogApplyButton}`,
      highlightId: TUTORIAL_IDS.SchedulesMainDialogApplyButton,
      title: t("Tutorials##create schedule##mobile##sixth step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##sixth step##content" />
      ),
      spotlightPadding: 2,
      placement: "bottom"
    },
    {
      target: TUTORIAL_IDS.Body,
      title: t("Tutorials##create schedule##mobile##final step##title"),
      content: (
        <Trans i18nKey="Tutorials##create schedule##mobile##final step##content" />
      ),
      placement: "center"
    }
  ];

  return {
    addNewCompanyCardStepsDesktop,
    addNewCompanyCardStepsMobile,
    addVehicleStep,
    applyScheduleStepsDesktop,
    applyScheduleStepsMobile,
    createScheduleStepsDesktop,
    createScheduleStepsMobile,
    companySelectorStepsDesktop,
    companySelectorStepsMobile
  };
};

export default useTutorialStepGroups;
